<template>
  <div :class="wrapperClasses">
    <FormLabel
      v-if="$slots.label"
      :tooltip="tooltip"
      :is-required="isRequired"
      :id="id"
      class="tw-mb-2"
    >
      <slot name="label" />
    </FormLabel>
    <div
      ref="selectWrapperRef"
      class="tw-form-multiselect tw-relative tw-rounded-md tw-border-0 tw-p-0 tw-shadow-sm"
    >
      <Multiselect
        v-model="value"
        v-bind="$attrs"
        :options="options"
        label="label"
        track-by="label"
        preserve-search
        :tabindex="$attrs.disabled ? -1 : 0"
        :test_id="$attrs.test_id"
        :allow-empty="allowEmpty"
      />
    </div>
    <div v-if="hasErrors || $slots.caption" class="tw-mt-2">
      <FormErrors v-if="hasErrors" :id="id" :errors="errors" />
      <FormCaption v-if="$slots.caption" :class="{ 'tw-mt-1': hasErrors }">
        <slot name="caption" />
      </FormCaption>
    </div>
  </div>
</template>

<script setup>
import Multiselect from 'vue-multiselect'
import FormLabel from './FormLabel.vue'
import { getUniqueId } from '@/shared/utils/uniqueId.js'
import FormErrors from './FormErrors.vue'
import FormCaption from './FormCaption.vue'
import { computed, ref, watch } from 'vue'

const emit = defineEmits(['update:modelValue', 'input'])

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  options: {
    type: Array,
    required: true,
  },
  isError: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: [Array, String],
    default: '',
  },
  wrapperClasses: {
    type: [Array, String, Object],
    default: () => [],
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
    default: '',
  },
  allowEmpty: {
    type: Boolean,
    default: true,
  },
})

const id = `growthmentor-select-${getUniqueId()}`
const value = ref(
  props.options.find((option) => option.value === props.modelValue),
)
const selectWrapperRef = ref(null)

const hasErrors = computed(() => {
  if (!props.isError) return false
  const isString = typeof props.errors === 'string'
  return isString ? props.errors : props.errors.length
})

watch(value, (newValue) => {
  emit('update:modelValue', newValue ? newValue.value : null)
  // 'input' emit is backward compatibility with Vue 2 v-model
  emit('input', newValue ? newValue.value : null)
})
watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) return
    value.value = props.options.find((option) => option.value === newValue)
  },
)
watch(
  () => props.isError,
  (newValue) => {
    if (newValue) {
      selectWrapperRef.value
        .querySelector('.multiselect__tags')
        .classList.add('multiselect__tags--error')
    } else {
      selectWrapperRef.value
        .querySelector('.multiselect__tags')
        .classList.remove('multiselect__tags--error')
    }
  },
)
</script>

<script>
export default {
  inheritAttrs: false,
}
</script>
